import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { GET_LIST, useClient } from '@peracto/client'
import { HydraTable, Heading, SelectFilter, TimeAgo } from '@peracto/peracto-ui'

import { RULE_TYPES } from './ruleTypes'

const SUBJECTS = {
    brand_rule: 'Brand rule',
    part_exception: 'Exception',
}

const SUBJECT_OPTIONS = Object.entries(SUBJECTS).map(([value, label]) => ({ label, value }))

const FIELD_LABELS = {
    brandName: 'Brand',
    supplierAccountCode: 'Supplier account',
    tecDocSupplierId: 'TecDoc brand number',
    ruleType: 'Rule',
    ruleValue: 'Prefix or suffix',
    exampleSomoraPartNumber: 'Example Somora part number',
    exampleTecDocPartNumber: 'Example TecDoc part number',
    notes: 'Notes',
    active: 'Active',
    somoraPartNumber: 'Somora part number',
    tecDocPartNumber: 'TecDoc part number',
}

const formatValue = value => {
    if (value === null || value === '') {
        return '(blank)'
    }

    return RULE_TYPES[value] || (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value))
}

const ChangeDetails = ({ action, changes }) => {
    if (action === 'deleted') {
        return '-'
    }

    return (
        <ul className="list-unstyled mb-0">
            {Object.entries(changes)
                .filter(([field]) => FIELD_LABELS[field])
                .map(([field, [before, after]]) => (
                    <li key={field}>
                        <strong>{FIELD_LABELS[field]}</strong>:{' '}
                        {action === 'created'
                            ? formatValue(after)
                            : `${formatValue(before)} → ${formatValue(after)}`}
                    </li>
                ))}
        </ul>
    )
}

const columns = [
    {
        Header: 'When',
        accessor: 'createdAt',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ cell: { value } }) => <TimeAgo date={value} />,
        selectable: false,
    },
    {
        Header: 'What',
        accessor: 'subject',
        disableSortBy: true,
        Cell: ({ row, cell: { value } }) =>
            row.original.action !== 'deleted' && row.original.ruleId ? (
                <Link to={`/tecdoc-brand-rules/${row.original.ruleId}`}>
                    {SUBJECTS[value]}: {row.original.summary}
                </Link>
            ) : (
                `${SUBJECTS[value]}: ${row.original.summary}`
            ),
        Filter: props => <SelectFilter label="What" options={SUBJECT_OPTIONS} {...props} />,
        selectable: false,
    },
    {
        Header: 'Change',
        accessor: 'action',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ cell: { value } }) => <span className="text-capitalize">{value}</span>,
        selectable: false,
    },
    {
        Header: 'Details',
        accessor: 'changes',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ row }) => <ChangeDetails action={row.original.action} changes={row.original.changes} />,
        selectable: false,
    },
    {
        Header: 'By',
        accessor: 'changedBy',
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ cell: { value } }) => value || '-',
        selectable: false,
    },
]

const RuleChangeList = () => {
    const { client } = useClient()
    const ruleId = new URLSearchParams(useLocation().search).get('ruleId')
    const onFetchData = options => client(GET_LIST, 'tecdoc-rule-changes', options)

    return (
        <>
            <Helmet>
                <title>TecDoc Brand Rule Changes | Peracto</title>
            </Helmet>
            <Heading name={ruleId ? 'Brand Rule Change History' : 'TecDoc Brand Rule Change History'}>
                <div className="d-flex align-items-center ml-2">
                    <Link
                        className="btn btn-outline-primary"
                        to={ruleId ? `/tecdoc-brand-rules/${ruleId}` : '/tecdoc-brand-rules'}
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
                        {ruleId ? 'Back to Rule' : 'Back to Brand Rules'}
                    </Link>
                </div>
            </Heading>
            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                key={ruleId || 'all'}
                                columns={columns}
                                onFetchData={onFetchData}
                                alwaysFilter={ruleId ? { ruleId } : {}}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default RuleChangeList
